/**
 * @flow
 */

import React, { useEffect, useState } from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { isNil } from 'lodash';
import { Modal, Button, Form, Icon, Message, Label } from 'semantic-ui-react';
import TimeSpanSelect from '../../containers/schedule/TimeSpanSelect';
import DeleteWorkingTimeButton from './DeleteWorkingTimeButton';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { convertMinutesToDateTime } from '../../../../lib/date';
import { GET_SALON_QUERY } from '../../containers/schedule/timetable/Timetable';

import type { ScheduleSettings, Master, WorkingTime, Intl } from '../../../../type';

type WorkingTimeDialogProps = {
    schedule: ScheduleSettings,
    master: Master,
    workingTime: WorkingTime,
    intl: Intl,
    trigger?: any,
    open: boolean,
    onClose: () => void,
};

const FormGroup = styled(Form.Group)`
    margin-bottom: 0px !important;
`;

const intlMessages = defineMessages({
    editTime: {
        id: 'pages.settings.schedule.form.editTime',
        defaultMessage: 'Edit time',
    },
    addTime: {
        id: 'pages.settings.schedule.form.addTime',
        defaultMessage: 'Add time',
    },
    startAtPlaceholder: {
        id: 'pages.settings.schedule.form.startWorkingAt',
        defaultMessage: 'Start working at',
    },
    endAtPlaceholder: {
        id: 'pages.settings.schedule.form.finishWorkingAt',
        defaultMessage: 'Finish working at',
    },
    endAtError: {
        id: 'pages.settings.schedule.form.endTimeError',
        defaultMessage: 'End time cannot be less then start time',
    },
    saveBtn: {
        id: 'pages.settings.schedule.form.save',
        defaultMessage: 'Save',
    },
    cancelBtn: {
        id: 'pages.settings.schedule.form.cancel',
        defaultMessage: 'Cancel',
    },
});

// Mutation to add new working time
const ADD_WORKING_TIME_MUTATION = gql`
    mutation addWorkingTime($input: AddWorkingTimeInput!) {
        addWorkingTime(input: $input) {
            workingTime {
                id
                startAt
                endAt
            }
        }
    }
`;

// Mutation to update existing working time
const UPDATE_WORKING_TIME_MUTATION = gql`
    mutation updateWorkingTime($input: UpdateWorkingTimeInput!) {
        updateWorkingTime(input: $input) {
            workingTime {
                id
                startAt
                endAt
            }
            updated
        }
    }
`;

const WorkingTimeDialog = ({
    trigger,
    open,
    onClose,
    schedule,
    master,
    workingTime,
    intl,
}: WorkingTimeDialogProps) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const date = useSelector(({ schedule: scheduleState }) =>
        scheduleState.get('date'),
    );
    const showArchive = useSelector(({ salon: salonState }) =>
        salonState.get('showArchive'),
    );
    const currentLanguage = useSelector(
        ({ intl: intlState }) =>
            intlState.get('locale') || intlState.get('defaultLanguage'),
    );

    const edit = workingTime && workingTime.id !== undefined;
    const [updated, setUpdated] = useState(true);

    const buildInitialValues = () => {
        if (!open || !workingTime) return { startAt: '', endAt: '' };
        return {
            startAt: convertMinutesToDateTime(workingTime.startAt, date).toISOString(),
            endAt: convertMinutesToDateTime(workingTime.endAt, date).toISOString(),
        };
    };

    const {
        control,
        handleSubmit,
        watch,
        reset,
        setError,
        formState: { errors, isSubmitting },
    } = useForm({ defaultValues: buildInitialValues() });

    useEffect(() => {
        if (open) {
            setUpdated(true);
            reset(buildInitialValues());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, workingTime, reset]);

    const startAtValue = watch('startAt');
    const endAtValue = watch('endAt');

    const [addWorkingTime] = useMutation(ADD_WORKING_TIME_MUTATION);
    const [updateWorkingTime] = useMutation(UPDATE_WORKING_TIME_MUTATION);

    const refetchVariables = {
        query: GET_SALON_QUERY,
        variables: {
            salon,
            showArchive,
            date: format(new Date(date), 'yyyy-MM-dd'),
            lang: currentLanguage,
        },
    };

    const onSubmit = async formData => {
        try {
            if (isNil(workingTime.id)) {
                await addWorkingTime({
                    variables: {
                        input: {
                            salon,
                            master: master.id,
                            startAt: format(new Date(formData.startAt), 'yyyy-MM-dd HH:mm'),
                            endAt: format(new Date(formData.endAt), 'yyyy-MM-dd HH:mm'),
                        },
                    },
                    refetchQueries: [refetchVariables],
                });
                onClose();
            } else {
                const result = await updateWorkingTime({
                    variables: {
                        input: {
                            workingTimeId: workingTime.id,
                            startAt: format(new Date(formData.startAt), 'yyyy-MM-dd HH:mm'),
                            endAt: format(new Date(formData.endAt), 'yyyy-MM-dd HH:mm'),
                        },
                    },
                    refetchQueries: [refetchVariables],
                });
                if (
                    result.data.updateWorkingTime &&
                    !result.data.updateWorkingTime.updated
                ) {
                    setUpdated(false);
                } else {
                    onClose();
                }
            }
        } catch (error) {
            setError('root', { message: String(error) });
        }
    };

    const handleClose = () => {
        setUpdated(true);
        onClose();
    };

    return (
        <Modal
            size="small"
            trigger={trigger}
            onClose={handleClose}
            open={open}
        >
            <Modal.Header>
                {edit
                    ? intl.formatMessage(intlMessages.editTime)
                    : intl.formatMessage(intlMessages.addTime)}
            </Modal.Header>
            <Modal.Content>
                {errors.root && (
                    <Message error>
                        <Icon name="warning sign" />
                        {errors.root.message}
                    </Message>
                )}
                {!updated && (
                    <Message
                        icon="warning sign"
                        negative
                        content={
                            <FormattedMessage
                                id="pages.settings.schedule.form.deleteForm.notUpdated"
                                defaultMessage="Schedule cannot be changed, because there are appointments for that day"
                            />
                        }
                    />
                )}
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <FormGroup widths="equal">
                        <Form.Field error={!!errors.startAt}>
                            <Controller
                                name="startAt"
                                control={control}
                                rules={{ required: true }}
                                render={({ field: { ref: _ref, value, onChange, ...field } }) => (
                                    <TimeSpanSelect
                                        {...field}
                                        name="startAt"
                                        value={value || undefined}
                                        onChange={(e, data) => onChange(data.value)}
                                        placeholder={intl.formatMessage(
                                            intlMessages.startAtPlaceholder,
                                        )}
                                        master={master}
                                        startAtValue={startAtValue}
                                        endAtValue={endAtValue}
                                    />
                                )}
                            />
                        </Form.Field>
                        <Form.Field error={!!errors.endAt}>
                            <Controller
                                name="endAt"
                                control={control}
                                rules={{ required: true }}
                                render={({ field: { ref: _ref, value, onChange, ...field } }) => (
                                    <TimeSpanSelect
                                        {...field}
                                        name="endAt"
                                        value={value || undefined}
                                        onChange={(e, data) => onChange(data.value)}
                                        placeholder={intl.formatMessage(
                                            intlMessages.endAtPlaceholder,
                                        )}
                                        master={master}
                                        startAtValue={startAtValue}
                                        endAtValue={endAtValue}
                                    />
                                )}
                            />
                            {errors.endAt && errors.endAt.message && (
                                <Label color="red" pointing basic>
                                    {errors.endAt.message}
                                </Label>
                            )}
                        </Form.Field>
                    </FormGroup>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                {workingTime && workingTime.id && (
                    <DeleteWorkingTimeButton
                        schedule={schedule}
                        master={master}
                        workingTime={workingTime}
                    />
                )}

                <Button
                    onClick={() => {
                        setUpdated(true);
                        handleSubmit(onSubmit)();
                    }}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    primary
                >
                    <Icon name="checkmark" />
                    {intl.formatMessage(intlMessages.saveBtn)}
                </Button>
                <Button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    basic
                >
                    <Icon name="remove" />
                    {intl.formatMessage(intlMessages.cancelBtn)}
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default injectIntl(WorkingTimeDialog);
